import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'

interface ElaraPanelState {
    isPanelOpen: boolean
    agentType: string
    sessionId: string | null
    togglePanel: () => void
    openPanel: () => void
    closePanel: () => void
    setAgentType: (type: string) => void
    setSessionId: (id: string | null) => void
    newChat: () => void
}

const ElaraPanelContext = createContext<ElaraPanelState | null>(null)

export function ElaraPanelProvider({ children }: { children: React.ReactNode }) {
    const [isPanelOpen, setIsPanelOpen] = useState(false)
    const [agentType, setAgentType] = useState('platform-supervisor')
    const [sessionId, setSessionId] = useState<string | null>(null)

    const togglePanel = useCallback(() => setIsPanelOpen((p) => !p), [])
    const openPanel = useCallback(() => setIsPanelOpen(true), [])
    const closePanel = useCallback(() => setIsPanelOpen(false), [])

    const newChat = useCallback(() => setSessionId(null), [])

    const handleSetAgentType = useCallback((type: string) => {
        setAgentType(type)
        setSessionId(null)
    }, [])

    // Ctrl+E to toggle panel
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'e' && e.ctrlKey && !e.altKey && !e.shiftKey) {
                e.preventDefault()
                setIsPanelOpen((p) => !p)
            }
        }
        window.addEventListener('keydown', handler)
        return () => window.removeEventListener('keydown', handler)
    }, [])

    return (
        <ElaraPanelContext.Provider
            value={{
                isPanelOpen,
                agentType,
                sessionId,
                togglePanel,
                openPanel,
                closePanel,
                setAgentType: handleSetAgentType,
                setSessionId,
                newChat,
            }}
        >
            {children}
        </ElaraPanelContext.Provider>
    )
}

export function useElaraPanel(): ElaraPanelState | null {
    return useContext(ElaraPanelContext)
}
